import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useI18n } from '../../lib/i18n';
import { rt } from '../../lib/rt';
import { CaptureFailed } from '../../lib/share/capture';
import { copyStyles, docPipSupported } from '../../lib/share/pip';
import { controlPopupUrl, newControlKey, openControlHost, type ControlCmd, type ControlHost, type ControlState } from '../../lib/rec/control';
import { extForMime } from '../../lib/rec/mime';
import {
  beginCapture, bitrateForLocation, createRecorder, defaultTitle, limitState, recordingSupported, type Recorder,
} from '../../lib/rec/recorder';
import { emitRecordingChange } from '../../lib/rec/store';
import { claimLabel, type CreateRecordingResponse, type PartsResponse, type RecordingDTO, type SendResponse } from '../../lib/rec/types';
import {
  Uploader, clearRecovery, estimateRemainingSec, loadRecovery, pendingSeconds, saveRecovery, shouldAutoPause,
  type Recovery, type UploaderState,
} from '../../lib/rec/uploader';
import { RecordSetupModal, type RecordSetup } from './RecordSetupModal';
import { RecordControlUI } from './RecordControl';
import { RecPill } from './RecPill';
import { RecordPreview } from './RecordPreview';
import { MyRecordings } from './MyRecordings';
import { RecordingViewerOverlay } from './RecordingViewerOverlay';

/**
 * EL CEREBRO DE RECORD (spec §4.12): un solo provider junto al ShareProvider en App.tsx. Guarda el
 * grabador, la cola de subida, la ventana de control y la vista previa; pinta el modal de
 * configuración (z 4000), la cuenta regresiva (z 4100), la píldora REC, los controles (portal al
 * Document PiP) y la vista previa con envío.
 *
 * Fases: idle → setup → starting (picker nativo abierto) → countdown → recording/paused → preview
 * (subida terminando, complete, envío) → idle.
 *
 * REGLA DE ORO: `start()` corre en el MISMO tick del clic de "Start recording". Adentro se llama,
 * en este orden y sin ningún await en el medio: PRIMERO la ventana de control
 * (`documentPictureInPicture.requestWindow`, o `window.open` del popup) y DESPUÉS `getDisplayMedia`.
 * Medido en el e2e (Chrome 149): `getDisplayMedia` CONSUME la activación transitoria — pedido al
 * revés, `requestWindow` tira "Document PiP requires user activation". Cualquier await antes pierde
 * el gesto (spec §4.12.1 punto 2).
 *
 * El video nunca pasa por el backend: los chunks de 1 s van a `Uploader` (multipart prefirmado a
 * R2) y también se guardan localmente para la vista previa y el Download local.
 */

export type RecPhase = 'idle' | 'setup' | 'starting' | 'countdown' | 'recording' | 'preview';

export type RecNotice =
  | { kind: 'nothing' | 'popupBlocked' | 'noMic' | 'micDenied' | 'limitReached' | 'failedUpload' | 'uploadStuck' | 'linkCopied' | 'recovered' | 'notSupported' | 'startFailed' | 'rateLimited' | 'error' | 'kept' | 'deleted'; message?: string }
  | { kind: 'sent'; n: number }
  | { kind: 'attached'; claimId: number; label: string };

export type PreviewStatus = 'uploading' | 'completing' | 'uploaded' | 'failed';

export type PreviewView = {
  id: number | null;
  title: string;
  blob: Blob | null;
  blobUrl: string | null;
  durationMs: number;
  mime: string;
  status: PreviewStatus;
  recording: RecordingDTO | null;
};

export type UploadView = UploaderState & { remainingSec: number | null };

type View = {
  phase: RecPhase;
  pausedBy: 'user' | 'net' | null;
  muted: boolean;
  hasMic: boolean;
  micState: 'on' | 'denied' | 'none' | null;
  hasSystemAudio: boolean;
  surface: ControlState['surface'];
  countdown: number;
  elapsedMs: number;
  limitSoon: boolean;
  upload: UploadView | null;
  preview: PreviewView | null;
  previewOpen: boolean;
  notice: RecNotice | null;
  mineOpen: boolean;
  /** El visor embebido en el centro (banner / sección /recordings / RecordingCard). */
  viewerId: number | null;
  popupBlocked: boolean;
  recovery: Recovery | null;
};

const initialView: View = {
  phase: 'idle', pausedBy: null, muted: false, hasMic: false, micState: null, hasSystemAudio: false, surface: null, countdown: 0,
  elapsedMs: 0, limitSoon: false, upload: null, preview: null, previewOpen: false, notice: null, mineOpen: false, viewerId: null, popupBlocked: false, recovery: null,
};

export type RecContext = View & {
  canRecord: boolean;
  /** El visor embebido está disponible (hay provider): la RecordingCard abre en el centro en vez de inline. */
  viewerAvailable: boolean;
  isRecording: boolean;
  openSetup(): void;
  closeSetup(): void;
  /** DEBE correr en el mismo tick del clic de Start. */
  start(setup: RecordSetup): void;
  pause(): void;
  resume(): void;
  toggleMute(): void;
  stop(): void;
  cancel(force?: boolean): void;
  openPreview(): void;
  closePreview(): void;
  setTitle(title: string): void;
  retryUpload(): void;
  sendToUsers(userIds: string[], departments?: string[]): Promise<void>;
  attachToClaim(claimId: number, department: string | null, body: string, label?: string): Promise<void>;
  deleteCurrent(): Promise<void>;
  recover(): Promise<void>;
  discardRecovery(): Promise<void>;
  openMine(): void;
  closeMine(): void;
  openViewer(id: number): void;
  closeViewer(): void;
  dismissNotice(): void;
  notify(n: RecNotice): void;
};

const Ctx = createContext<RecContext | null>(null);

const COUNTDOWN_FROM = 3;
const TICK_MS = 1_000;

function errNotice(e: unknown): RecNotice {
  if (e instanceof ApiError) {
    if (e.status === 429) return { kind: 'rateLimited' };
    if (e.message && /\s/.test(e.message)) return { kind: 'error', message: e.message };
  }
  return { kind: 'error' };
}

type DocPipWin = Window & { document: Document };

export function RecProvider({ children }: { children?: ReactNode }) {
  const { me } = useAuth();
  const { t } = useI18n();
  const user = me?.kind === 'tenant' ? me.user : null;
  const canRecord = !!user && user.record_enabled === true && user.role !== 'inspector' && recordingSupported();

  const [view, setView] = useState<View>(initialView);
  const viewRef = useRef(view);
  viewRef.current = view;
  const patch = useCallback((p: Partial<View> | ((v: View) => Partial<View>)) => {
    setView((v) => ({ ...v, ...(typeof p === 'function' ? p(v) : p) }));
  }, []);

  const recorderRef = useRef<Recorder | null>(null);
  const uploaderRef = useRef<Uploader | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const hostRef = useRef<ControlHost | null>(null);
  const recIdRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);
  const [docPipWin, setDocPipWin] = useState<DocPipWin | null>(null);
  const docPipRef = useRef<DocPipWin | null>(null);
  const tRef = useRef(t);
  tRef.current = t;
  const stopRef = useRef<() => void>(() => {});
  const cancelRef = useRef<(force?: boolean) => void>(() => {});
  const retryRef = useRef<() => void>(() => {});
  /** `partsSent` con el que resolvió `finish()`: si lo que falló fue `complete`, se repite con esto. */
  const partsSentRef = useRef<number | null>(null);

  const closeControlWindow = useCallback(() => {
    const w = docPipRef.current;
    docPipRef.current = null;
    setDocPipWin(null);
    if (w) { try { w.close(); } catch { /* */ } }
    hostRef.current?.close();
    hostRef.current = null;
  }, []);

  const clearTimers = useCallback(() => {
    if (tickRef.current != null) { clearInterval(tickRef.current); tickRef.current = null; }
    if (countdownRef.current != null) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  /** Lo que ve la ventana de control (Doc PiP, popup o píldora). */
  const controlState = useMemo<ControlState>(() => ({
    phase: view.phase === 'starting' ? 'starting' : view.phase === 'countdown' ? 'countdown' : view.phase === 'recording' ? (view.pausedBy ? 'paused' : 'recording') : 'done',
    pausedBy: view.pausedBy,
    muted: view.muted,
    hasMic: view.hasMic,
    elapsedMs: view.elapsedMs,
    countdown: view.countdown,
    surface: view.surface,
    limitSoon: view.limitSoon,
    uploadFailed: view.upload?.failed === true,
  }), [view.countdown, view.elapsedMs, view.hasMic, view.limitSoon, view.muted, view.pausedBy, view.phase, view.surface, view.upload?.failed]);

  // Cada cambio del estado de control se publica al popup (BroadcastChannel); el Doc PiP lo ve por el portal.
  useEffect(() => { hostRef.current?.postState(controlState); }, [controlState]);

  // ───────────────────────── recuperación ─────────────────────────

  useEffect(() => {
    if (!canRecord) return;
    const r = loadRecovery();
    if (r) patch({ recovery: r });
  }, [canRecord, patch]);

  // ───────────────────────── socket: recording.ready ─────────────────────────

  useEffect(() => {
    if (!user) return undefined;
    const offs = [
      rt.on('recording.ready', (m) => {
        emitRecordingChange(m.recording.id, m.recording);
        patch((v) => (v.preview && v.preview.id === m.recording.id ? { preview: { ...v.preview, recording: m.recording } } : {}));
      }),
      rt.on('recording.failed', (m) => { emitRecordingChange(m.recording.id, m.recording); }),
    ];
    return () => { for (const off of offs) off(); };
  }, [patch, user]);

  // ───────────────────────── subida ─────────────────────────

  const onUploadState = useCallback((s: UploaderState) => {
    patch((v) => {
      const upload = { ...s, remainingSec: estimateRemainingSec(s.pendingBytes, s.bytesPerSec) };
      if (!s.failed || !v.preview || v.preview.status === 'failed') return { upload };
      // La cola se rindió (5 intentos). Se marca SIN mirar la fase: `finish()` emite en el mismo tick
      // del Stop, cuando `viewRef` todavía dice 'recording', y la vista previa quedaba en
      // "Uploading a/b…" para siempre sin Retry. Grabando, el aviso es "pulsa Retry upload"; en la
      // vista previa, "descárgala antes de cerrar".
      const notice: RecNotice = v.phase === 'preview' ? { kind: 'failedUpload' } : { kind: 'uploadStuck' };
      return { upload, preview: { ...v.preview, status: 'failed' }, notice };
    });
  }, [patch]);

  const persistRecovery = useCallback((partsDone: number) => {
    const id = recIdRef.current;
    const rec = recorderRef.current;
    if (id == null || !rec) return;
    saveRecovery({ id, partsUploaded: partsDone, startedAt: Date.now() - rec.elapsedMs(), recordedMs: rec.elapsedMs(), mime: rec.mime, title: viewRef.current.preview?.title ?? null });
  }, []);

  /** Tras `finish()`: POST complete → la fila queda `uploaded` y el worker la remuxea. */
  const completeUpload = useCallback(async (partsSent: number) => {
    const id = recIdRef.current;
    const rec = recorderRef.current;
    if (id == null) return;
    patch((v) => ({ preview: v.preview ? { ...v.preview, status: 'completing' } : null }));
    try {
      const size = rec?.size() ?? { width: null, height: null };
      const r = await api.post<{ recording: RecordingDTO }>(`/api/recordings/${id}/complete`, {
        partsSent,
        durationMs: viewRef.current.preview?.durationMs ?? rec?.elapsedMs() ?? undefined,
        width: size.width ?? undefined,
        height: size.height ?? undefined,
      });
      clearRecovery(id);
      emitRecordingChange(id, r.recording);
      patch((v) => ({ preview: v.preview ? { ...v.preview, status: 'uploaded', recording: r.recording } : null }));
    } catch (e) {
      patch((v) => ({ preview: v.preview ? { ...v.preview, status: 'failed' } : null, notice: errNotice(e) }));
    }
  }, [patch]);

  const finishUpload = useCallback(async () => {
    const up = uploaderRef.current;
    if (!up) return;
    let partsSent: number;
    try { partsSent = await up.finish(); }
    catch { return; /* abortada (cancel/delete) */ }
    partsSentRef.current = partsSent;
    await completeUpload(partsSent);
  }, [completeUpload]);

  // ───────────────────────── parar / cancelar ─────────────────────────

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    const v = viewRef.current;
    if (!rec || (v.phase !== 'recording' && v.phase !== 'countdown')) return;
    clearTimers();
    const wasCountdown = v.phase === 'countdown';
    void (async () => {
      await rec.stop();
      rec.release();
      closeControlWindow();
      const durationMs = rec.elapsedMs();
      if (wasCountdown || durationMs < 500 || chunksRef.current.length === 0) {
        // Paró antes de grabar nada: no hay vista previa que mostrar.
        uploaderRef.current?.abort();
        const id = recIdRef.current;
        if (id != null) { void api.post(`/api/recordings/${id}/delete`, {}).catch(() => {}); clearRecovery(id); }
        recorderRef.current = null; uploaderRef.current = null; recIdRef.current = null; chunksRef.current = [];
        patch({ phase: 'idle', notice: { kind: 'nothing' }, upload: null, preview: null, pausedBy: null, elapsedMs: 0, limitSoon: false });
        return;
      }
      const blob = new Blob(chunksRef.current, { type: rec.mime });
      const blobUrl = URL.createObjectURL(blob);
      patch((cur) => ({
        phase: 'preview', previewOpen: true, pausedBy: null,
        preview: { id: recIdRef.current, title: cur.preview?.title ?? defaultTitle(new Date(), tRef.current), blob, blobUrl, durationMs, mime: rec.mime, status: 'uploading', recording: null },
      }));
      await finishUpload();
    })();
  }, [clearTimers, closeControlWindow, finishUpload, patch]);
  stopRef.current = stop;

  const resetAll = useCallback(() => {
    clearTimers();
    recorderRef.current?.release();
    recorderRef.current = null;
    uploaderRef.current?.abort();
    uploaderRef.current = null;
    closeControlWindow();
    const p = viewRef.current.preview;
    if (p?.blobUrl) { try { URL.revokeObjectURL(p.blobUrl); } catch { /* */ } }
    chunksRef.current = [];
    recIdRef.current = null;
    partsSentRef.current = null;
    patch({ phase: 'idle', pausedBy: null, muted: false, upload: null, preview: null, previewOpen: false, elapsedMs: 0, limitSoon: false, countdown: 0, popupBlocked: false, surface: null });
  }, [clearTimers, closeControlWindow, patch]);

  const cancel = useCallback((force = false) => {
    const v = viewRef.current;
    if (v.phase === 'idle' || v.phase === 'setup') return;
    if (!force && !window.confirm(tRef.current('rec.cancelConfirm'))) return;
    cancelledRef.current = true;
    const id = recIdRef.current;
    if (id != null) { void api.post(`/api/recordings/${id}/delete`, {}).catch(() => {}); clearRecovery(id); }
    resetAll();
  }, [resetAll]);
  cancelRef.current = cancel;

  // ───────────────────────── arrancar ─────────────────────────

  const openSetup = useCallback(() => {
    if (!canRecord) { patch({ notice: { kind: 'notSupported' } }); return; }
    if (viewRef.current.phase !== 'idle') return;
    patch({ phase: 'setup', notice: null });
  }, [canRecord, patch]);
  const closeSetup = useCallback(() => { if (viewRef.current.phase === 'setup') patch({ phase: 'idle' }); }, [patch]);

  const start = useCallback((setup: RecordSetup) => {
    if (!canRecord || (viewRef.current.phase !== 'setup' && viewRef.current.phase !== 'idle')) return;
    cancelledRef.current = false;
    chunksRef.current = [];
    recIdRef.current = null;
    const key = newControlKey();

    // 1) LA VENTANA DE CONTROL, PRIMERO y en el mismo clic: Document PiP donde exista; si no, el
    //    popup con BroadcastChannel (noopener: `window.open` devuelve null, así que "abrió" se sabe
    //    por el `hello`). Va antes de la captura porque `getDisplayMedia` consume la activación.
    let pipPromise: Promise<DocPipWin> | null = null;
    let popupTried = false;
    if (docPipSupported()) {
      try {
        const api2 = (window as unknown as { documentPictureInPicture: { requestWindow(o: { width: number; height: number }): Promise<DocPipWin> } }).documentPictureInPicture;
        pipPromise = api2.requestWindow({ width: 320, height: 140 });
        pipPromise.catch(() => {}); // se espera más abajo; sin esto un rechazo sería "unhandled"
      } catch { pipPromise = null; }
    }
    if (!pipPromise) {
      try { window.open(controlPopupUrl(key), 'ppag-rec', 'popup,noopener,width=340,height=190'); popupTried = true; }
      catch { popupTried = false; }
    }

    // 2) LA CAPTURA, SÍNCRONA: el picker nativo nace dentro de la activación del clic.
    let capPromise: ReturnType<typeof beginCapture>;
    try { capPromise = beginCapture({ withMic: setup.withMic, micDeviceId: setup.micDeviceId ?? undefined, withSystemAudio: setup.withSystemAudio }); }
    catch (e) { capPromise = Promise.reject(e); }
    const host = openControlHost(key);
    hostRef.current = host;
    host.onCmd((cmd: ControlCmd) => {
      switch (cmd) {
        case 'pause': pauseRef.current(); break;
        case 'resume': resumeRef.current(); break;
        case 'mute': setMutedRef.current(true); break;
        case 'unmute': setMutedRef.current(false); break;
        case 'stop': stopRef.current(); break;
        case 'cancel': cancelRef.current(true); break;
        case 'retry': retryRef.current(); break;
        default: break;
      }
    });
    patch({ phase: 'starting', notice: null, popupBlocked: false, elapsedMs: 0, limitSoon: false, pausedBy: null, muted: false, preview: null, upload: null });

    void (async () => {
      let cap: Awaited<typeof capPromise>;
      try { cap = await capPromise; }
      catch (e) {
        // Canceló el picker (o el permiso de macOS): se cierra la ventana de control y no queda nada.
        if (pipPromise) { void pipPromise.then((w) => { try { w.close(); } catch { /* */ } }).catch(() => {}); }
        host.close(); hostRef.current = null;
        const kind = e instanceof CaptureFailed ? e.kind : 'failed';
        patch({ phase: 'idle', notice: kind === 'cancelled' ? { kind: 'nothing' } : kind === 'unsupported' ? { kind: 'notSupported' } : { kind: 'startFailed', message: e instanceof Error ? e.message : undefined } });
        return;
      }
      if (cancelledRef.current) { cap.stop(); return; }

      let pipWin: DocPipWin | null = null;
      if (pipPromise) { try { pipWin = await pipPromise; } catch { pipWin = null; } }
      if (pipWin) {
        copyStyles(pipWin.document);
        pipWin.document.title = tRef.current('rec.controlTitle');
        pipWin.document.body.className = 'rec-pip-body';
        docPipRef.current = pipWin;
        setDocPipWin(pipWin);
        pipWin.addEventListener('pagehide', () => { if (docPipRef.current === pipWin) { docPipRef.current = null; setDocPipWin(null); } });
      } else if (popupTried) {
        void host.waitHello().then((ok) => { if (!ok && hostRef.current === host) patch({ popupBlocked: true, notice: { kind: 'popupBlocked' } }); });
      } else {
        patch({ popupBlocked: true, notice: { kind: 'popupBlocked' } });
      }

      const rec = createRecorder(cap, { videoKbps: bitrateForLocation(user?.time_location_name) });
      if (!rec) {
        cap.stop();
        closeControlWindow();
        patch({ phase: 'idle', notice: { kind: 'startFailed' } });
        return;
      }
      recorderRef.current = rec;
      const micNotice: RecNotice | null = cap.mic === 'denied' ? { kind: 'micDenied' } : setup.withMic && cap.mic === 'none' ? { kind: 'noMic' } : null;
      patch((v) => ({ hasMic: rec.hasMic, micState: cap.mic, hasSystemAudio: rec.hasSystemAudio, surface: rec.surface, notice: micNotice ?? v.notice }));

      // La fila en el servidor (status uploading) + el multipart. El video sigue sin haberse grabado.
      let created: CreateRecordingResponse;
      try {
        created = await api.post<CreateRecordingResponse>('/api/recordings', {
          mime: rec.mime, ua: navigator.userAgent, surface: rec.surface ?? undefined,
          hasMic: rec.hasMic, hasSystemAudio: rec.hasSystemAudio, hasCamera: false, title: defaultTitle(new Date(), tRef.current),
        });
      } catch (e) {
        rec.release();
        closeControlWindow();
        recorderRef.current = null;
        patch({ phase: 'idle', notice: errNotice(e) });
        return;
      }
      if (cancelledRef.current) { rec.release(); void api.post(`/api/recordings/${created.id}/delete`, {}).catch(() => {}); return; }
      recIdRef.current = created.id;
      const id = created.id;
      const up = new Uploader({
        partSize: created.partSize,
        presign: (from, count) => api.post<PartsResponse>(`/api/recordings/${id}/parts`, { from, count }).then((r) => r.urls),
        onState: onUploadState,
        onPartDone: persistRecovery,
      });
      uploaderRef.current = up;
      rec.onData((b) => { chunksRef.current.push(b); up.push(b); });
      rec.onEnded(() => { if (recorderRef.current === rec) stopRef.current(); });
      patch({ preview: { id, title: defaultTitle(new Date(), tRef.current), blob: null, blobUrl: null, durationMs: 0, mime: rec.mime, status: 'uploading', recording: null } });

      // 3) CUENTA REGRESIVA 3-2-1 y arranque de verdad.
      patch({ phase: 'countdown', countdown: COUNTDOWN_FROM });
      let n = COUNTDOWN_FROM;
      countdownRef.current = window.setInterval(() => {
        n -= 1;
        if (n > 0) { patch({ countdown: n }); return; }
        if (countdownRef.current != null) { clearInterval(countdownRef.current); countdownRef.current = null; }
        if (cancelledRef.current || recorderRef.current !== rec) return;
        try { rec.start(); }
        catch (e) {
          rec.release(); closeControlWindow(); recorderRef.current = null;
          void api.post(`/api/recordings/${id}/delete`, {}).catch(() => {});
          patch({ phase: 'idle', notice: { kind: 'startFailed', message: e instanceof Error ? e.message : undefined } });
          return;
        }
        saveRecovery({ id, partsUploaded: 0, startedAt: Date.now(), recordedMs: 0, mime: rec.mime, title: null });
        patch({ phase: 'recording', countdown: 0, elapsedMs: 0 });
        // El tick: cronómetro, tope de 30 min, auto-pausa por segundos pendientes.
        tickRef.current = window.setInterval(() => {
          const r = recorderRef.current;
          const u = uploaderRef.current;
          if (!r || r !== rec) return;
          const elapsed = r.elapsedMs();
          const lim = limitState(elapsed);
          patch({ elapsedMs: elapsed, limitSoon: lim.warn && !lim.reached });
          if (lim.reached) { patch({ notice: { kind: 'limitReached' } }); stopRef.current(); return; }
          if (u) {
            const cur = viewRef.current;
            const pendingSec = pendingSeconds(u.state.pendingBytes, u.state.totalBytes, elapsed);
            const byNet = cur.pausedBy === 'net';
            const want = shouldAutoPause(pendingSec, byNet);
            if (want && !cur.pausedBy) { r.pause(); patch({ pausedBy: 'net' }); }
            else if (!want && byNet) { r.resume(); patch({ pausedBy: null }); }
          }
        }, TICK_MS);
      }, 1000);
    })();
  }, [canRecord, closeControlWindow, onUploadState, patch, persistRecovery, user?.time_location_name]);

  // ───────────────────────── pausa / mute ─────────────────────────

  const pause = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || viewRef.current.phase !== 'recording' || viewRef.current.pausedBy) return;
    rec.pause();
    patch({ pausedBy: 'user' });
  }, [patch]);
  const resume = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || viewRef.current.pausedBy !== 'user') return;
    rec.resume();
    patch({ pausedBy: null });
  }, [patch]);
  const setMuted = useCallback((m: boolean) => {
    recorderRef.current?.setMuted(m);
    patch({ muted: m });
  }, [patch]);
  const toggleMute = useCallback(() => setMuted(!viewRef.current.muted), [setMuted]);
  const pauseRef = useRef(pause); pauseRef.current = pause;
  const resumeRef = useRef(resume); resumeRef.current = resume;
  const setMutedRef = useRef(setMuted); setMutedRef.current = setMuted;

  // Al volver la red, una pausa por subida se reevalúa en el próximo tick (el uploader ya reanuda solo).
  useEffect(() => {
    const on = () => { const v = viewRef.current; if (v.pausedBy === 'net' && recorderRef.current) { recorderRef.current.resume(); patch({ pausedBy: null }); } };
    window.addEventListener('online', on);
    return () => window.removeEventListener('online', on);
  }, [patch]);

  // ───────────────────────── vista previa: título, envío, borrado ─────────────────────────

  const openPreview = useCallback(() => { if (viewRef.current.preview) patch({ previewOpen: true }); }, [patch]);
  const closePreview = useCallback(() => {
    const v = viewRef.current;
    const pending = uploaderRef.current?.pending === true || v.preview?.status === 'uploading' || v.preview?.status === 'completing';
    if (!pending || v.preview?.status === 'failed') {
      // Subida terminada (o rendida): se suelta el Blob local y se vuelve a idle.
      if (v.preview?.status === 'failed' && !window.confirm(tRef.current('rec.failedUploadLeave'))) return;
      resetAll();
      return;
    }
    // Sigue subiendo en el fondo: la píldora lo muestra y reabre la vista previa.
    patch({ previewOpen: false });
  }, [patch, resetAll]);

  const setTitle = useCallback((title: string) => {
    patch((v) => ({ preview: v.preview ? { ...v.preview, title } : null }));
    const id = recIdRef.current;
    const clean = title.trim();
    if (id != null && clean) {
      void api.patch(`/api/recordings/${id}`, { title: clean }).then(() => {
        patch((v) => ({ preview: v.preview?.recording ? { ...v.preview, recording: { ...v.preview.recording, title: clean } } : v.preview }));
      }).catch(() => {});
    }
  }, [patch]);

  const retryUpload = useCallback(() => {
    const up = uploaderRef.current;
    if (!up) return;
    patch((v) => ({ preview: v.preview && v.preview.status === 'failed' ? { ...v.preview, status: 'uploading' } : v.preview, notice: null }));
    if (up.state.failed) { up.retry(); return; }
    // La cola ya había terminado: lo que falló fue `POST /complete` (502 de un deploy, pool lleno).
    // `up.retry()` era un no-op y la barra volvía a "Uploading…" sin que nada pasara. Se repite el
    // complete, que es idempotente en el backend.
    if (up.state.done && partsSentRef.current != null) void completeUpload(partsSentRef.current);
  }, [completeUpload, patch]);
  retryRef.current = retryUpload;

  const sendToUsers = useCallback(async (userIds: string[], departments?: string[]) => {
    const id = recIdRef.current;
    const depts = [...new Set((departments ?? []).map((d) => d.trim()).filter(Boolean))];
    if (id == null || (!userIds.length && !depts.length)) return;
    try {
      // El backend expande cada departamento a sus miembros ACTIVOS (UNION user_departments +
      // teams.department/team_members) → DM + fila recipients `via='dept'`, sin duplicar a quien
      // ya iba por `userIds` ni al que envía (recipients PK recording_id+user_id).
      const r = await api.post<SendResponse>(`/api/recordings/${id}/send`, { userIds, ...(depts.length ? { departments: depts } : {}) });
      emitRecordingChange(id, r.recording);
      patch((v) => ({ preview: v.preview ? { ...v.preview, recording: r.recording } : null, notice: { kind: 'sent', n: r.sentTo } }));
    } catch (e) { patch({ notice: errNotice(e) }); throw e; }
  }, [patch]);

  const attachToClaim = useCallback(async (claimId: number, department: string | null, body: string, label?: string) => {
    const id = recIdRef.current;
    if (id == null) return;
    try {
      const r = await api.post<SendResponse>(`/api/recordings/${id}/send`, { claimId, department: department ?? undefined, body: body.trim() || undefined });
      emitRecordingChange(id, r.recording);
      patch((v) => ({ preview: v.preview ? { ...v.preview, recording: r.recording } : null, notice: { kind: 'attached', claimId, label: claimLabel(r.recording.claim) || label || `#${claimId}` } }));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'contractor_not_allowed') patch({ notice: { kind: 'error', message: tRef.current('rec.contractorNo') } });
      else if (e instanceof ApiError && e.code === 'already_attached') patch({ notice: { kind: 'error', message: tRef.current('rec.alreadyAttached') } });
      else patch({ notice: errNotice(e) });
      throw e;
    }
  }, [patch]);

  const deleteCurrent = useCallback(async () => {
    const id = recIdRef.current;
    if (!window.confirm(tRef.current('rec.deleteConfirm'))) return;
    if (id != null) { try { await api.post(`/api/recordings/${id}/delete`, {}); emitRecordingChange(id, null); } catch { /* best-effort */ } clearRecovery(id); }
    resetAll();
    patch({ notice: { kind: 'deleted' } });
  }, [patch, resetAll]);

  // ───────────────────────── recuperación ─────────────────────────

  const recover = useCallback(async () => {
    const r = viewRef.current.recovery;
    if (!r) return;
    try {
      const res = await api.post<{ recording: RecordingDTO }>(`/api/recordings/${r.id}/complete`, { partsSent: r.partsUploaded, durationMs: r.recordedMs || undefined });
      clearRecovery(r.id);
      emitRecordingChange(r.id, res.recording);
      patch({ recovery: null, notice: { kind: 'recovered' }, mineOpen: true });
    } catch (e) {
      // R2 no tiene las partes contiguas (o ninguna): no hay video que armar. Se deja la fila y la
      // recuperación en pie para que "Discard" la limpie.
      if (e instanceof ApiError && e.code === 'parts_missing') patch({ notice: { kind: 'error', message: tRef.current('rec.recoverIncomplete') } });
      else patch({ notice: errNotice(e) });
    }
  }, [patch]);

  const discardRecovery = useCallback(async () => {
    const r = viewRef.current.recovery;
    if (!r) return;
    try { await api.post(`/api/recordings/${r.id}/delete`, {}); } catch { /* ya no está */ }
    clearRecovery(r.id);
    patch({ recovery: null });
  }, [patch]);

  const openMine = useCallback(() => patch({ mineOpen: true }), [patch]);
  const closeMine = useCallback(() => patch({ mineOpen: false }), [patch]);
  const openViewer = useCallback((id: number) => { if (Number.isFinite(id)) patch({ viewerId: id }); }, [patch]);
  const closeViewer = useCallback(() => patch({ viewerId: null }), [patch]);
  const dismissNotice = useCallback(() => patch({ notice: null }), [patch]);
  const notify = useCallback((n: RecNotice) => patch({ notice: n }), [patch]);

  // ───────────────────────── beforeunload ─────────────────────────

  const busy = view.phase === 'recording' || view.phase === 'countdown' || view.phase === 'starting'
    || (view.phase === 'preview' && (view.preview?.status === 'uploading' || view.preview?.status === 'completing'));
  useEffect(() => {
    if (!busy) return undefined;
    const onBefore = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [busy]);

  // Al desmontar (logout) se suelta todo.
  useEffect(() => () => { recorderRef.current?.release(); closeControlWindow(); clearTimers(); }, [clearTimers, closeControlWindow]);

  const ctl = useCallback((cmd: ControlCmd) => {
    switch (cmd) {
      case 'pause': pause(); break;
      case 'resume': resume(); break;
      case 'mute': setMuted(true); break;
      case 'unmute': setMuted(false); break;
      case 'stop': stop(); break;
      case 'cancel': cancel(true); break;
      case 'retry': retryUpload(); break;
      default: break;
    }
  }, [cancel, pause, resume, retryUpload, setMuted, stop]);

  const value = useMemo<RecContext>(() => ({
    ...view, canRecord, viewerAvailable: true, isRecording: view.phase === 'recording' || view.phase === 'countdown' || view.phase === 'starting',
    openSetup, closeSetup, start, pause, resume, toggleMute, stop, cancel, openPreview, closePreview, setTitle, retryUpload,
    sendToUsers, attachToClaim, deleteCurrent, recover, discardRecovery, openMine, closeMine, openViewer, closeViewer, dismissNotice, notify,
  }), [view, canRecord, openSetup, closeSetup, start, pause, resume, toggleMute, stop, cancel, openPreview, closePreview, setTitle, retryUpload, sendToUsers, attachToClaim, deleteCurrent, recover, discardRecovery, openMine, closeMine, openViewer, closeViewer, dismissNotice, notify]);

  const ext = view.preview ? extForMime(view.preview.mime) : 'webm';

  return (
    <Ctx.Provider value={value}>
      {children}
      {user && (
        <>
          {view.phase === 'setup' && <RecordSetupModal onStart={start} onClose={closeSetup} />}
          {view.phase === 'countdown' && (
            <div className="rec-countdown" role="status" aria-live="assertive">
              <div className="rec-countdown-num" key={view.countdown}>{view.countdown}</div>
              <div className="rec-countdown-text">{t('rec.countdown', { n: view.countdown })}</div>
            </div>
          )}
          <RecPill />
          {docPipWin && createPortal(<RecordControlUI state={controlState} on={ctl} inWindow />, docPipWin.document.body)}
          {view.phase === 'preview' && view.previewOpen && view.preview && <RecordPreview ext={ext} />}
          {view.mineOpen && <MyRecordings onClose={closeMine} />}
          {view.viewerId != null && <RecordingViewerOverlay id={view.viewerId} onClose={closeViewer} />}
          <RecNoticeToast />
        </>
      )}
    </Ctx.Provider>
  );
}

const noCtx: RecContext = {
  ...initialView, canRecord: false, viewerAvailable: false, isRecording: false,
  openSetup() {}, closeSetup() {}, start() {}, pause() {}, resume() {}, toggleMute() {}, stop() {}, cancel() {}, openPreview() {}, closePreview() {},
  setTitle() {}, retryUpload() {}, async sendToUsers() {}, async attachToClaim() {}, async deleteCurrent() {}, async recover() {}, async discardRecovery() {},
  openMine() {}, closeMine() {}, openViewer() {}, closeViewer() {}, dismissNotice() {}, notify() {},
};

/** Tolerante: fuera del provider (ventanas sin shell) devuelve un contexto inerte. */
export function useRec(): RecContext {
  return useContext(Ctx) ?? noCtx;
}

export function recNoticeText(n: RecNotice, t: (k: string, vars?: Record<string, string | number>) => string): string {
  switch (n.kind) {
    case 'nothing': return t('rec.nothing');
    case 'popupBlocked': return t('rec.popupBlocked');
    case 'noMic': return t('rec.noMic');
    case 'micDenied': return t('share.micDenied');
    case 'limitReached': return t('rec.limitReached');
    case 'failedUpload': return t('rec.failedUpload');
    case 'uploadStuck': return t('rec.uploadStuck');
    case 'linkCopied': return t('rec.linkCopied');
    case 'recovered': return t('rec.recovered');
    case 'notSupported': return t('rec.notSupported');
    case 'startFailed': return n.message ? `${t('rec.startFailed')} — ${n.message}` : t('rec.startFailed');
    case 'rateLimited': return t('share.rateLimited');
    case 'kept': return t('rec.kept');
    case 'deleted': return t('rec.deleted');
    case 'sent': return t('rec.sentTo', { n: n.n });
    case 'attached': return t('rec.attached', { claim: n.label });
    case 'error': return n.message || t('rec.error');
    default: return '';
  }
}

/** Aviso transitorio (abajo de la barra, como el del share). */
function RecNoticeToast() {
  const r = useRec();
  const { t } = useI18n();
  const n = r.notice;
  useEffect(() => {
    if (!n) return undefined;
    const id = window.setTimeout(() => r.dismissNotice(), n.kind === 'failedUpload' || n.kind === 'uploadStuck' || n.kind === 'popupBlocked' ? 15_000 : 6_000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
  if (!n) return null;
  const warn = n.kind === 'failedUpload' || n.kind === 'uploadStuck' || n.kind === 'popupBlocked' || n.kind === 'error' || n.kind === 'startFailed' || n.kind === 'notSupported';
  return (
    <div className={`ss-toast rec-toast${warn ? ' warn' : ''}`} role="status" onClick={r.dismissNotice}>
      <span>{recNoticeText(n, t)}</span>
    </div>
  );
}
